/**
 * CRM lead scoring — thresholds calibrated for Saudi market (SAR).
 * Legacy DZD equivalents were ~10× higher; removed Algerian-specific criteria.
 */
import { CRM_DEFAULT_CURRENCY } from './crmService';

/** Estimated deal value breakpoints (SAR). */
export const LEAD_VALUE_THRESHOLDS_SAR = {
  hot: 50000,
  warm: 10000,
  cold: 0,
};

/** Score weights (max ~100). */
const WEIGHTS = {
  estimatedValue: 35,
  source: 20,
  engagement: 25,
  recency: 20,
};

/** Source quality scores for Saudi B2B context. */
const SOURCE_SCORES = {
  referral: 20,
  partner: 18,
  exhibition: 16,
  website: 14,
  linkedin: 12,
  cold_call: 8,
  email: 10,
  social: 9,
  other: 6,
};

/** Days since last contact → recency score. */
function scoreRecency(lastContactAt) {
  if (!lastContactAt) return 8;
  const days = Math.floor((Date.now() - new Date(lastContactAt).getTime()) / 86400000);
  if (days <= 3) return 20;
  if (days <= 7) return 16;
  if (days <= 14) return 12;
  if (days <= 30) return 8;
  return 4;
}

function resolveLeadValueThresholds(config = {}) {
  const hot = parseFloat(config.highValueLeadThreshold);
  const warm = parseFloat(config.standardCustomerThreshold);
  return {
    hot: Number.isFinite(hot) && hot > 0 ? hot : LEAD_VALUE_THRESHOLDS_SAR.hot,
    warm: Number.isFinite(warm) && warm > 0 ? warm : LEAD_VALUE_THRESHOLDS_SAR.warm,
  };
}

/** Map estimated value (SAR) to partial score. */
function scoreEstimatedValue(value, config = {}) {
  const thresholds = resolveLeadValueThresholds(config);
  const n = parseFloat(value) || 0;
  if (n >= thresholds.hot) return WEIGHTS.estimatedValue;
  if (n >= thresholds.warm) return Math.round(WEIGHTS.estimatedValue * 0.65);
  if (n > 0) return Math.round(WEIGHTS.estimatedValue * 0.35);
  return 5;
}

/** Engagement from interaction count / status. */
function scoreEngagement(lead) {
  const status = (lead.status || '').toLowerCase();
  if (status === 'qualified') return WEIGHTS.engagement;
  if (status === 'contacted') return Math.round(WEIGHTS.engagement * 0.7);
  if (status === 'new') return Math.round(WEIGHTS.engagement * 0.4);
  if (status === 'won') return WEIGHTS.engagement;
  if (status === 'lost') return 0;
  return Math.round(WEIGHTS.engagement * 0.5);
}

/**
 * Compute lead score 0–100 for Saudi CRM pipeline prioritization.
 * @param {object} lead - { estimated_value, source, status, last_contact_at?, interaction_count? }
 */
export function computeLeadScore(lead = {}, config = {}) {
  const sourceKey = (lead.source || 'other').toLowerCase().replace(/\s+/g, '_');
  const sourceScore = SOURCE_SCORES[sourceKey] ?? SOURCE_SCORES.other;

  const total =
    scoreEstimatedValue(lead.estimated_value, config) +
    sourceScore +
    scoreEngagement(lead) +
    scoreRecency(lead.last_contact_at);

  return Math.min(100, Math.max(0, Math.round(total)));
}

/** Tier label from score. */
export function getLeadScoreTier(score) {
  if (score >= 75) return 'hot';
  if (score >= 50) return 'warm';
  if (score >= 25) return 'nurture';
  return 'cold';
}

/** Arabic tier labels for UI. */
export const LEAD_SCORE_TIER_LABELS_AR = {
  hot: 'ساخن — أولوية عالية',
  warm: 'دافئ — متابعة نشطة',
  nurture: 'رعاية — متابعة دورية',
  cold: 'بارد — متابعة لاحقة',
};

/** Sort leads by score descending. */
export function rankLeadsByScore(leads = [], config = {}) {
  return [...leads]
    .map((lead) => ({
      ...lead,
      leadScore: computeLeadScore(lead, config),
      leadScoreTier: getLeadScoreTier(computeLeadScore(lead, config)),
      currency: lead.currency || CRM_DEFAULT_CURRENCY,
    }))
    .sort((a, b) => b.leadScore - a.leadScore);
}

export default {
  LEAD_VALUE_THRESHOLDS_SAR,
  computeLeadScore,
  getLeadScoreTier,
  LEAD_SCORE_TIER_LABELS_AR,
  rankLeadsByScore,
};
